import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type Area = 'CRM' | 'Trading' | 'Admin' | null;

interface PageTitleContextValue {
    /** Area suffix, set once by the active layout (e.g. "CRM"). */
    setArea: (area: Area) => void;
    /** Page-specific prefix, set by PageHeader. */
    setPageTitle: (title: string | null) => void;
    area: Area;
    pageTitle: string | null;
}

const PageTitleContext = createContext<PageTitleContextValue | undefined>(undefined);

export const PageTitleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [area, setArea] = useState<Area>(null);
    const [pageTitle, setPageTitle] = useState<string | null>(null);

    const value = useMemo(() => ({ area, pageTitle, setArea, setPageTitle }), [area, pageTitle]);

    return <PageTitleContext.Provider value={value}>{children}</PageTitleContext.Provider>;
};

function usePageTitleContext(): PageTitleContextValue | undefined {
    return useContext(PageTitleContext);
}

/**
 * Declares which application area is mounted. Called once by each layout so the
 * document title can read "<page> · <tenant> CRM" regardless of the route path
 * (CRM and Trading both live at "/", so the path alone cannot tell them apart).
 */
export function useRegisterArea(area: Exclude<Area, null>): void {
    const ctx = usePageTitleContext();
    const setArea = ctx?.setArea;

    useEffect(() => {
        if (!setArea) return;
        setArea(area);
        return () => setArea(null);
    }, [area, setArea]);
}

/**
 * Sets the page-specific part of the document title for as long as the caller is
 * mounted. PageHeader calls this, so every page that uses it is titled correctly.
 */
export function useRegisterPageTitle(title: string | null | undefined): void {
    const ctx = usePageTitleContext();
    const setPageTitle = ctx?.setPageTitle;

    useEffect(() => {
        if (!setPageTitle) return;
        setPageTitle(title ?? null);
        return () => setPageTitle(null);
    }, [title, setPageTitle]);
}

/** Read the composed title parts. Used by PageTitleSetter. */
export function usePageTitleParts(): { area: Area; pageTitle: string | null } {
    const ctx = usePageTitleContext();
    return { area: ctx?.area ?? null, pageTitle: ctx?.pageTitle ?? null };
}

/** Imperative setter for pages that need to override the title without PageHeader. */
export function useSetPageTitle(): (title: string | null) => void {
    const ctx = usePageTitleContext();
    const setPageTitle = ctx?.setPageTitle;
    return useCallback(
        (title: string | null) => {
            setPageTitle?.(title);
        },
        [setPageTitle]
    );
}
